import React, { useCallback, useEffect, useRef, useState } from 'react';
// 既定クラス (.throttled-image-wrap / .throttled-image) と `--loaded` 修飾子は
// 実行時に文字列連結で組み立てるため Tailwind ユーティリティでは表現できない。
// スタイル本体は src/styles/globals.css の @layer components に移設済み。

// 同一 CloudFront 接続への画像リクエストのバーストが HTTP/2 ストリーム切断
// (net::ERR_HTTP2_PROTOCOL_ERROR) を起こすため、モジュールスコープのセマフォで
// 同時ロード数を制限する。ページ内の全 ThrottledImage で 1 個のセマフォを共有。
const MAX_CONCURRENT_IMAGE_LOADS = 6;
let activeImageLoads = 0;
const imageLoadQueue: Array<() => void> = [];

function acquireImageLoadSlot(): Promise<void> {
  if (activeImageLoads < MAX_CONCURRENT_IMAGE_LOADS) {
    activeImageLoads += 1;
    return Promise.resolve();
  }
  return new Promise<void>((resolve) => {
    imageLoadQueue.push(() => {
      activeImageLoads += 1;
      resolve();
    });
  });
}

function releaseImageLoadSlot(): void {
  activeImageLoads = Math.max(0, activeImageLoads - 1);
  const next = imageLoadQueue.shift();
  if (next) {
    next();
  }
}

interface ThrottledImageProps {
  src: string;
  alt: string;
  /** ラッパー div に付与する基底クラス（`--loaded` 修飾子も自動付与される） */
  wrapperClassName?: string;
  /** img に付与する基底クラス（`--loaded` 修飾子も自動付与される） */
  imgClassName?: string;
  /** IntersectionObserver の先読みマージン */
  rootMargin?: string;
}

/**
 * 可視域に近づいてから、同時ロード数を最大 6 件に絞って読み込む画像コンポーネント。
 * onerror 時は同一 URL のまま 1 回だけ自動リトライ（immutable キャッシュを壊さないため
 * cache-bust は付けない）。ロード中はシマー、完了でフェードイン（CSS はクラス側で表現）。
 */
const ThrottledImage: React.FC<ThrottledImageProps> = ({
  src,
  alt,
  wrapperClassName = 'throttled-image-wrap',
  imgClassName = 'throttled-image',
  rootMargin = '200px',
}) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [nearViewport, setNearViewport] = useState(false);
  const [activeSrc, setActiveSrc] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [failed, setFailed] = useState(false);

  const holdingSlotRef = useRef(false);
  const retriedRef = useRef(false);
  const retryTimerRef = useRef<number | null>(null);
  const cancelledRef = useRef(false);

  const releaseSlot = useCallback(() => {
    if (holdingSlotRef.current) {
      holdingSlotRef.current = false;
      releaseImageLoadSlot();
    }
  }, []);

  // src が変わったら状態をリセット（リスト再利用対策）
  useEffect(() => {
    cancelledRef.current = false;
    retriedRef.current = false;
    setActiveSrc(null);
    setLoaded(false);
    setFailed(false);
    return () => {
      cancelledRef.current = true;
      if (retryTimerRef.current) {
        window.clearTimeout(retryTimerRef.current);
      }
      releaseSlot();
    };
  }, [src, releaseSlot]);

  // 可視域に近づいたら読み込み対象にする
  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) {
      return;
    }
    if (typeof IntersectionObserver === 'undefined') {
      setNearViewport(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setNearViewport(true);
          io.disconnect();
        }
      },
      { rootMargin }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [rootMargin, src]);

  // 可視域に入ったらセマフォを取得してから src をセット
  useEffect(() => {
    if (!nearViewport || activeSrc !== null || failed) {
      return;
    }
    let done = false;
    acquireImageLoadSlot().then(() => {
      if (cancelledRef.current || done) {
        releaseImageLoadSlot();
        return;
      }
      holdingSlotRef.current = true;
      setActiveSrc(src);
    });
    return () => {
      done = true;
    };
  }, [nearViewport, activeSrc, failed, src]);

  const handleLoad = useCallback(() => {
    setLoaded(true);
    releaseSlot();
  }, [releaseSlot]);

  const handleError = useCallback(() => {
    releaseSlot();
    if (!retriedRef.current && !cancelledRef.current) {
      retriedRef.current = true;
      const delay = 500 + Math.floor(Math.random() * 500);
      retryTimerRef.current = window.setTimeout(() => {
        if (cancelledRef.current) {
          return;
        }
        // 同一 URL のまま再取得（activeSrc を null に戻すと取得 effect が再実行される）
        setActiveSrc(null);
      }, delay);
    } else {
      setFailed(true);
    }
  }, [releaseSlot]);

  const wrapperClass = `${wrapperClassName}${loaded ? ` ${wrapperClassName}--loaded` : ''}`;
  const imgClass = `${imgClassName}${loaded ? ` ${imgClassName}--loaded` : ''}`;

  return (
    <div ref={wrapperRef} className={wrapperClass}>
      {activeSrc && !failed && (
        <img
          className={imgClass}
          src={activeSrc}
          alt={alt}
          decoding="async"
          onLoad={handleLoad}
          onError={handleError}
        />
      )}
      {failed && (
        <div
          className="throttled-image-error"
          role="img"
          aria-label={alt}
          style={{ width: '100%', minHeight: 120, background: '#f3f4f6' }}
        />
      )}
    </div>
  );
};

export default ThrottledImage;
